import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  RefreshCw,
  Download,
  Search,
  LogOut,
  Star,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'reset_admin_token';

const Stars = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${
          value >= n ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-zinc-700'
        }`}
      />
    ))}
  </div>
);

const TEXT_FIELDS = [
  { key: 'venue_comments', label: 'Venue comments' },
  { key: 'favorite_session', label: 'Most moving' },
  { key: 'next_year_ideas', label: 'Next year ideas' },
  { key: 'improvements', label: 'Improvements' },
  { key: 'additional_comments', label: 'Other comments' },
];

export default function AdminFeedback() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialToken =
    searchParams.get('token') || localStorage.getItem(TOKEN_KEY) || '';

  const [token, setToken] = useState(initialToken);
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchRows = async (t) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/feedback/retreat`, {
        headers: { 'X-Admin-Token': t },
      });
      if (res.status === 401) {
        setError('Invalid admin token. Try again.');
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        return;
      }
      if (!res.ok) {
        setError(`Error ${res.status}. Please retry.`);
        return;
      }
      const data = await res.json();
      setRows(data.responses || []);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      if (searchParams.get('token')) {
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });
      }
    } catch (e) {
      setError('Network error. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialToken) fetchRows(initialToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      TEXT_FIELDS.some((f) => (r[f.key] || '').toLowerCase().includes(q)) ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q),
    );
  }, [rows, query]);

  const stats = useMemo(() => {
    if (rows.length === 0) {
      return { avgOverall: 0, avgVenue: 0, recommendPct: 0 };
    }
    const avgOverall =
      rows.reduce((s, r) => s + (r.overall_rating || 0), 0) / rows.length;
    const avgVenue =
      rows.reduce((s, r) => s + (r.venue_rating || 0), 0) / rows.length;
    const yes = rows.filter((r) => r.would_recommend).length;
    return {
      avgOverall: avgOverall.toFixed(1),
      avgVenue: avgVenue.toFixed(1),
      recommendPct: Math.round((yes / rows.length) * 100),
    };
  }, [rows]);

  const downloadCsv = () => {
    const header = [
      'Submitted',
      'Overall (1-5)',
      'Would Recommend',
      'Venue (1-5)',
      'Venue comments',
      'Most moving',
      'Next year ideas',
      'Improvements',
      'Additional comments',
      'Name',
      'Email',
    ];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.map(escape).join(',')];
    filtered.forEach((r) => {
      lines.push(
        [
          r.created_at,
          r.overall_rating,
          r.would_recommend ? 'Yes' : 'No',
          r.venue_rating,
          r.venue_comments,
          r.favorite_session,
          r.next_year_ideas,
          r.improvements,
          r.additional_comments,
          r.name,
          r.email,
        ]
          .map(escape)
          .join(','),
      );
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `retreat-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    fetchRows(token.trim());
  };
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setRows([]);
  };

  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6"
        data-testid="admin-feedback-login"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md bg-[#111111] border border-white/10 rounded-2xl p-8 md:p-10"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-cormorant text-3xl font-medium text-white text-center mb-2">
            Admin Access
          </h1>
          <p className="text-zinc-400 font-manrope text-sm text-center mb-6">
            Enter your admin token to view retreat feedback.
          </p>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12 mb-4"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <p className="text-red-400 font-manrope text-sm mb-4 text-center">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading || !token.trim()}
            className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? 'Verifying…' : 'Unlock'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="admin-feedback-page"
    >
      <header className="border-b border-white/5 bg-[#0A0A0A]/90 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center font-cormorant font-bold text-lg">
              R
            </div>
            <div>
              <p className="text-white font-cormorant text-lg leading-tight">
                Reset Admin
              </p>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest">
                Retreat Feedback
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/admin/surveys"
              className="text-zinc-400 hover:text-white font-manrope text-sm transition-colors"
            >
              Wealth surveys →
            </a>
            <button
              onClick={() => fetchRows(token)}
              disabled={loading}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white font-manrope text-sm transition-colors disabled:opacity-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-red-400 font-manrope text-sm transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-4">
            <p className="text-zinc-400 font-manrope text-[11px] uppercase tracking-widest">
              Total Responses
            </p>
            <p className="font-cormorant text-3xl font-medium text-white mt-1">
              {rows.length}
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#111111] p-4">
            <p className="text-zinc-400 font-manrope text-[11px] uppercase tracking-widest">
              Avg Overall
            </p>
            <p className="font-cormorant text-3xl font-medium text-white mt-1">
              {stats.avgOverall}
            </p>
            <p className="text-zinc-500 font-manrope text-[11px]">out of 5</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#111111] p-4">
            <p className="text-zinc-400 font-manrope text-[11px] uppercase tracking-widest">
              Avg Venue
            </p>
            <p className="font-cormorant text-3xl font-medium text-white mt-1">
              {stats.avgVenue}
            </p>
            <p className="text-zinc-500 font-manrope text-[11px]">out of 5</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#111111] p-4">
            <p className="text-zinc-400 font-manrope text-[11px] uppercase tracking-widest">
              Would Recommend
            </p>
            <p className="font-cormorant text-3xl font-medium text-white mt-1">
              {stats.recommendPct}%
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search comments, name, or email…"
              className="pl-10 bg-[#111111] border-white/10 text-white placeholder:text-zinc-600 h-11"
              data-testid="admin-search-input"
            />
          </div>
          <Button
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            className="bg-[#D4AF37] text-[#0A0A0A] px-5 h-11 rounded-md font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="admin-download-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* Cards list */}
        <div className="space-y-4">
          {filtered.length === 0 && (
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-12 text-center text-zinc-500 font-manrope text-sm">
              No feedback yet. Once responses come in, they&apos;ll show up here.
            </div>
          )}
          {filtered.map((r, i) => {
            const isOpen = expanded === (r.id || i);
            return (
              <div
                key={r.id || i}
                className="bg-[#111111] border border-white/10 rounded-2xl overflow-hidden"
                data-testid={`feedback-card-${i}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : r.id || i)}
                  className="w-full p-5 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span className="text-white font-manrope font-semibold">
                          {r.name || 'Anonymous brother'}
                        </span>
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-zinc-400 font-manrope text-sm hover:text-[#D4AF37]"
                          >
                            {r.email}
                          </a>
                        )}
                        <span className="text-zinc-600 font-manrope text-xs">
                          {new Date(r.created_at).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-5 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
                            Overall
                          </span>
                          <Stars value={r.overall_rating} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
                            Venue
                          </span>
                          <Stars value={r.venue_rating} />
                        </div>
                        <span
                          className={`font-manrope text-xs px-3 py-1 rounded-full border ${
                            r.would_recommend
                              ? 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10'
                              : 'text-zinc-500 border-white/10'
                          }`}
                        >
                          {r.would_recommend ? 'Recommends ✓' : "Wouldn't recommend"}
                        </span>
                      </div>
                    </div>
                    <span className="text-zinc-500 font-manrope text-xs">
                      {isOpen ? '▲ Hide details' : '▼ Show comments'}
                    </span>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-white/5 p-5 space-y-4 bg-black/40">
                    {TEXT_FIELDS.map((f) => (
                      <div key={f.key}>
                        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest mb-1">
                          {f.label}
                        </p>
                        <p className="text-zinc-300 font-manrope text-sm leading-relaxed whitespace-pre-wrap">
                          {r[f.key] || <span className="text-zinc-600 italic">— (no answer)</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-zinc-600 font-manrope text-xs text-center mt-8">
          Showing {filtered.length} of {rows.length} responses · newest first
        </p>
      </main>
    </div>
  );
}
